/**
 * Resolver akses form berbasis (unit_kategori, profesi).
 *
 * Sumber aturan: `src/data/rbac/form-access.data.ts` (static, matriks Direksi RSPS).
 * Klasifikasi unit: `src/utils/unit.ts` (helper kanonik dari hardcode codebase).
 *
 * Pendekatan **frontend-only**: gating untuk UX, bukan security. Server-side
 * authorization tetap di-handle oleh middleware backend yang sudah ada (token,
 * EditWindowService, Gate existing).
 */

import * as Unit from "@/utils/unit";

export type FormAction = "view" | "create" | "edit" | "sign" | "print" | "report";

export interface AccessRule {
  /** Token kategori unit yang berhak akses (lihat `resolveUnitKategori` untuk daftar token). */
  unitKategori: string;
  /** Nama profesi (string dari `User.profesi`). Empty string = match by unit only. */
  profesi: string;
  /** Set action yang di-grant kalau (unitKategori, profesi) match. */
  actions: FormAction[];
}

// Lazy loader supaya test bisa override / mock.
let _rulesCache: Record<string, AccessRule[]> | null = null;

function getRules(): Record<string, AccessRule[]> {
  if (_rulesCache) return _rulesCache;
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const mod = require("@/data/rbac/form-access.data");
  _rulesCache = mod.FORM_ACCESS_RULES;
  return _rulesCache as Record<string, AccessRule[]>;
}

/**
 * Override aturan untuk testing. Pass `null` untuk reset ke generated default.
 */
export function __setRulesForTesting(
  rules: Record<string, AccessRule[]> | null
): void {
  _rulesCache = rules;
}

export const SEMUA_UNIT = "*";

function unitCocok(userGroups: string[], unitKategori: string): boolean {
  return unitKategori === SEMUA_UNIT || userGroups.includes(unitKategori);
}

export function resolveUnitKategori(
  unitId: string | null | undefined,
  roleLegacyId?: number | null,
  extraKategori?: string[] | null
): string[] {
  const groups: string[] = [];
  const k = (unitId || "").trim();

  if (Unit.isGadar(k)) groups.push("gadar");
  if (Unit.isRalan(k)) groups.push("ralan");
  if (Unit.isRanap(k)) groups.push("ranap");
  if (Unit.isIntensif(k)) groups.push("intensif");
  if (Unit.isPenunjang(k)) groups.push("penunjang");
  if (Unit.isAdmin(k)) groups.push("admin");

  if (Unit.isLaboratorium(k)) groups.push("lab");
  if (Unit.isRadiologi(k)) groups.push("radiologi");
  if (Unit.isIBS(k)) groups.push("ibs");
  if (Unit.isHemodialisa(k)) groups.push("hd");
  if (Unit.isKemoterapi(k)) groups.push("kemoterapi");

  if (Unit.isRuangBersalin(k) || k === "08" || k === "2F") {
    groups.push("bersalin");
  }

  if (k === "30") groups.push("farmasi");
  if (k === "39") groups.push("jenazah");
  if (k === "2N") groups.push("forensik");
  if (k === "80") groups.push("rekam_medis");
  if (k === "81") groups.push("admisi");
  if (k === "82") groups.push("kasir");
  if (k === "83") groups.push("gizi");
  if (Unit.isUnitAdministrasi(k)) groups.push("administrasi");
  if (k === "98") groups.push("manajemen");

  // role legacy: 1=Direktur, 2=Wadir/Manajer (lihat App.vue:507 pola existing)
  if (typeof roleLegacyId === "number" && roleLegacyId > 0 && roleLegacyId <= 2) {
    if (!groups.includes("manajemen")) groups.push("manajemen");
  }

  if (roleLegacyId === 4) {
    ["tim_ppi", "tim_kprs"].forEach(t => {
      if (!groups.includes(t)) groups.push(t);
    });
  }

  // Token dari flag server (bukan dari unit login yang bisa diganti user),
  // mis. 'kepala_ruang' dari `unit_scope.is_kepala_ruang` pada payload login.
  (extraKategori || []).forEach(t => {
    if (t && !groups.includes(t)) groups.push(t);
  });

  return groups;
}

/**
 * Cek apakah user boleh akses $formCode dengan $action.
 *
 * Pemakaian:
 *   canAccessForm('01', 'Dokter', 1, 'visum_et_repertum', 'sign') → true
 *   canAccessForm('25', 'Perawat', 5, 'visum_et_repertum', 'view') → false
 */
export function canAccessForm(
  unitId: string | null | undefined,
  profesi: string | null | undefined,
  roleLegacyId: number | null | undefined,
  formCode: string,
  action: FormAction = "view",
  extraKategori?: string[] | null
): boolean {
  // Lazy import supaya circular import aman (form-access.data import dari sini).
  const rules = getRules()[formCode];
  if (!rules || rules.length === 0) return false;

  const userGroups = resolveUnitKategori(
    unitId,
    roleLegacyId ?? undefined,
    extraKategori
  );
  const userProfesi = (profesi || "").trim();

  for (const rule of rules) {
    if (!unitCocok(userGroups, rule.unitKategori)) continue;
    if (rule.profesi !== "" && rule.profesi !== userProfesi) continue;
    if (!rule.actions.includes(action)) continue;
    return true;
  }
  return false;
}

/**
 * Daftar form_code yang user bisa view. Dipakai untuk sidebar gating + audit.
 */
export function accessibleForms(
  unitId: string | null | undefined,
  profesi: string | null | undefined,
  roleLegacyId: number | null | undefined,
  action: FormAction = "view",
  extraKategori?: string[] | null
): string[] {
  const rules = getRules();
  const out: string[] = [];
  for (const code of Object.keys(rules)) {
    if (canAccessForm(unitId, profesi, roleLegacyId, code, action, extraKategori)) {
      out.push(code);
    }
  }
  return out;
}

/**
 * Matrix form → allowed actions. Lebih kaya dari accessibleForms, untuk
 * per-action gating tanpa iterasi.
 */
export function accessMatrix(
  unitId: string | null | undefined,
  profesi: string | null | undefined,
  roleLegacyId: number | null | undefined
): Record<string, FormAction[]> {
  const rules = getRules();
  const userGroups = resolveUnitKategori(unitId, roleLegacyId ?? undefined);
  const userProfesi = (profesi || "").trim();
  const out: Record<string, FormAction[]> = {};

  for (const [code, ruleList] of Object.entries(rules)) {
    const allowed = new Set<FormAction>();
    for (const r of ruleList) {
      if (!unitCocok(userGroups, r.unitKategori)) continue;
      if (r.profesi !== "" && r.profesi !== userProfesi) continue;
      for (const a of r.actions) allowed.add(a);
    }
    if (allowed.size > 0) out[code] = [...allowed];
  }
  return out;
}

/**
 * Label Indonesia untuk token unit_kategori (untuk pesan ke pengguna).
 * Selaras dengan doc-comment `resolveUnitKategori`.
 */
export const UNIT_KATEGORI_LABELS: Record<string, string> = {
  [SEMUA_UNIT]: "Semua unit",
  gadar: "IGD",
  ralan: "Rawat Jalan (Poliklinik)",
  ranap: "Rawat Inap (Bangsal)",
  intensif: "Intensif (ICU/HCU/PICU/ICCU)",
  penunjang: "Penunjang Medis",
  admin: "Administrasi",
  farmasi: "Farmasi",
  lab: "Laboratorium",
  radiologi: "Radiologi",
  ibs: "Bedah Sentral (IBS)",
  hd: "Hemodialisa",
  kemoterapi: "Kemoterapi",
  jenazah: "Pemulasaraan Jenazah",
  bersalin: "Kebidanan / Bersalin (VK)",
  forensik: "Forensik & Medikolegal",
  rekam_medis: "Rekam Medis",
  admisi: "Pendaftaran / Admisi",
  kasir: "Kasir",
  administrasi: "Unit Administrasi",
  gizi: "Gizi",
  manajemen: "Manajemen / Direksi",
  tim_ppi: "Tim PPI",
  tim_kprs: "Tim Mutu / KPRS",
};

export function unitKategoriLabel(token: string): string {
  return UNIT_KATEGORI_LABELS[token] || token;
}

export type AccessDenyReason =
  | "granted"
  | "no_rule"
  | "unit_mismatch"
  | "profesi_mismatch";

export interface AccessDiagnosis {
  hasAccess: boolean;
  /**
   * Alasan gagal akses:
   *   - granted          : user berhak (hasAccess true).
   *   - no_rule          : form tidak punya aturan untuk action ini.
   *   - unit_mismatch    : profesi user berhak, tapi hanya di unit tertentu; unit user tidak termasuk.
   *   - profesi_mismatch : profesi user tidak termasuk yang berhak.
   */
  reason: AccessDenyReason;
  userProfesi: string;
  /** Token kategori unit user saat ini (mis. ['ralan']). */
  userUnitKategori: string[];
  /** Token unit yang mengizinkan profesi user (dipakai saat reason = unit_mismatch). */
  allowedUnitKategoriForProfesi: string[];
}

/**
 * Diagnosa kenapa user tidak/berhasil akses form — untuk pesan UX yang spesifik
 * (bedakan restriksi profesi vs restriksi unit). Logika match identik `canAccessForm`.
 */
export function diagnoseAccess(
  unitId: string | null | undefined,
  profesi: string | null | undefined,
  roleLegacyId: number | null | undefined,
  formCode: string,
  action: FormAction = "view"
): AccessDiagnosis {
  const userGroups = resolveUnitKategori(unitId, roleLegacyId ?? undefined);
  const userProfesi = (profesi || "").trim();
  const rules = getRules()[formCode] || [];
  const actionRules = rules.filter((r) => r.actions.includes(action));

  const base = {
    userProfesi,
    userUnitKategori: userGroups,
    allowedUnitKategoriForProfesi: [] as string[],
  };

  const granted = actionRules.some(
    (r) =>
      unitCocok(userGroups, r.unitKategori) &&
      (r.profesi === "" || r.profesi === userProfesi)
  );
  if (granted) {
    return { hasAccess: true, reason: "granted", ...base };
  }
  if (actionRules.length === 0) {
    return { hasAccess: false, reason: "no_rule", ...base };
  }

  // Profesi user cocok di suatu aturan? Kalau ya, blokir murni karena unit.
  const profesiMatchRules = actionRules.filter(
    (r) => r.profesi === "" || r.profesi === userProfesi
  );
  if (profesiMatchRules.length > 0) {
    const allowedUnits = Array.from(
      new Set(profesiMatchRules.map((r) => r.unitKategori))
    );
    return {
      hasAccess: false,
      reason: "unit_mismatch",
      ...base,
      allowedUnitKategoriForProfesi: allowedUnits,
    };
  }
  return { hasAccess: false, reason: "profesi_mismatch", ...base };
}

